import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { selectHasLoadError, selectIsLoadingProject } from '@ff-os/pdp-shared-redux/slices/projectLoadingSlice';
import { Alert } from '@ff-os/shared-react/components/alert/Alert';
import { Button } from '@ff-os/shared-react/components/buttons/button/Button';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { Spinner } from '@ff-os/shared-react/components/loaders/spinner/Spinner';
import { WindowStackProvider } from '@ff-os/shared-react/context/window-stack/WindowStack';
import { GiftPdpDispatch, GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { selectShowProjectIsIncompatible, setIsGiftWindowOpen } from '../_redux-ui';
import { ArQrCodeStackItem } from '../preview/ArQrCodeStackItem';
import { GiftWindowStackItem } from './GiftWindowStackItem';
import styles from './WindowedGiftDesignerUi.module.scss';

export const _WindowGiftDesignerUi = ({
    isLoadingProject, hasLoadError,
    setIsOpen, showProjectIsIncompatible
}: Props) => {

    const { t } = useTranslation();

    const tryOpen = (value: boolean) => {
        if (!isLoadingProject) {
            setIsOpen(value);
        }
    };

    return (
        <WindowStackProvider>
            <Grid gap='s' direction='column'>

                {hasLoadError && (
                    <Cell>
                        <Alert icon='alert-triangle-exclamation' type='error'>
                            {t('load_project.error_message')}
                        </Alert>
                    </Cell>
                )}

                {showProjectIsIncompatible && (
                    <Cell>
                        <Alert icon='alert-triangle-exclamation' type='warning'>
                            {t('load_project.incompatible_message')}
                        </Alert>
                    </Cell>
                )}

                <Cell>
                    <Button
                        className='idt-button--product-page'
                        onClick={() => tryOpen(true)}
                        disabled={isLoadingProject}>
                        {isLoadingProject ? (
                            <Spinner color='white' overlaySize='auto' spinnerSize='small'
                                className={styles.spinner} />
                        ) : (
                            <Icon type='general-ui-brush' size='small' strokeWidth={2}
                                className={styles.icon} />
                        )}
                        {t('start_designing')}
                    </Button>
                </Cell>

                <GiftWindowStackItem setIsOpen={setIsOpen} />

                <ArQrCodeStackItem />
            </Grid>
        </WindowStackProvider>
    );
};

type Props = {
    isLoadingProject: boolean;
    hasLoadError: boolean;
    setIsOpen: (value: boolean) => void;
    showProjectIsIncompatible: boolean;
};

const mapStateToProps = (state: GiftPdpState) => ({
    isLoadingProject: selectIsLoadingProject(state),
    hasLoadError: selectHasLoadError(state),
    showProjectIsIncompatible: selectShowProjectIsIncompatible(state)
});

const mapDispatchToProps = (dispatch: GiftPdpDispatch) => ({
    setIsOpen: (value: boolean) => dispatch(setIsGiftWindowOpen(value))
});

export const WindowGiftDesignerUi = connect(
    mapStateToProps,
    mapDispatchToProps
)(_WindowGiftDesignerUi);